"use client";

import { useRouter } from "next/navigation";
import { useTaskContext } from "@/contexts/TaskContext";

const PJK = "'Plus Jakarta Sans', var(--font-plus-jakarta), sans-serif";

export interface AgentCardData {
  id: string;
  name: string;
  avatarInitials: string;
  rating: number;
  tasksCompleted: number;
  tags: string[];
  status: "available" | "offline";
  price: number;
  isBestMatch?: boolean;
}

export default function AgentCard({
  id,
  name,
  avatarInitials,
  rating,
  tasksCompleted,
  tags,
  status,
  price,
  isBestMatch,
  onSelect,
}: AgentCardData & { onSelect?: (id: string) => void }) {
  const router = useRouter();
  const ctx = useTaskContext();
  const bookmarked = ctx.isBookmarked(id);

  const handleSelect = () => {
    if (onSelect) {
      onSelect(id);
    } else {
      router.push(`/app/agent/${id}`);
    }
  };

  const handleBookmark = (e: React.MouseEvent) => {
    e.stopPropagation();
    ctx.toggleBookmark(id);
  };

  return (
    <div
      className={`${isBestMatch ? "app-card-hover-best" : "app-card-hover"} desktop-glass-agent-card`}
      style={{
        background: "rgba(23,23,23,0.8)",
        border: isBestMatch
          ? "2px solid #D5BAFF"
          : "1px solid rgba(75,68,85,0.3)",
        borderRadius: 16,
        padding: isBestMatch ? 22 : 21,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        alignItems: "flex-start",
        position: "relative",
      }}
    >
      {isBestMatch && (
        <>
          <div
            style={{
              position: "absolute",
              top: -2,
              left: -2,
              right: -2,
              bottom: -1.5,
              borderRadius: 16,
              boxShadow: "0px 0px 40px -10px rgba(134,53,246,0.15)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: -12.75,
              left: "50%",
              transform: "translateX(-50%)",
              width: 103.503,
              background: "#D5BAFF",
              borderRadius: 9999,
              padding: "4px 12px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontFamily: PJK,
                fontWeight: 700,
                fontSize: 10,
                lineHeight: "15px",
                letterSpacing: "1px",
                textTransform: "uppercase",
                color: "#8635F6",
              }}
            >
              BEST MATCH
            </span>
          </div>
        </>
      )}

      {/* Bookmark (top-right corner, above rating badge) */}
      <button
        onClick={handleBookmark}
        aria-label={bookmarked ? "Remove bookmark" : "Bookmark agent"}
        title={bookmarked ? "Remove bookmark" : "Bookmark agent"}
        className="app-bookmark-btn"
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          width: 28,
          height: 28,
          background: "rgba(23,23,23,0.6)",
          border: "none",
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
        }}
      >
        <img
          src="/app-icons/bookmark.svg"
          alt=""
          style={{
            width: 14,
            height: 14,
            filter: bookmarked
              ? "invert(35%) sepia(94%) saturate(5800%) hue-rotate(260deg) brightness(101%) contrast(97%)"
              : "invert(75%)",
            opacity: bookmarked ? 1 : 0.5,
          }}
        />
      </button>

      {/* Top row: avatar + rating */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: isBestMatch ? "rgba(134,53,246,0.2)" : "#2A2A2A",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              fontFamily: PJK,
              fontWeight: 700,
              fontSize: isBestMatch ? 20 : 18,
              color: isBestMatch ? "#8635F6" : "#E5E2E1",
            }}
          >
            {avatarInitials}
          </span>
        </div>

        <div
          style={{
            background: "#201F1F",
            borderRadius: 6,
            padding: "4px 8px",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <img
            src="/app-icons/star-mini.svg"
            alt=""
            className="app-rating-star"
            style={{ width: 10, height: 9.5, transition: "filter 200ms ease-out" }}
          />
          <span
            style={{
              fontFamily: PJK,
              fontWeight: 700,
              fontSize: 10,
              lineHeight: "15px",
              color: "#E5E2E1",
            }}
          >
            {rating.toFixed(1)}
          </span>
        </div>
      </div>

      {/* Name + tasks completed */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          width: "100%",
        }}
      >
        <span
          style={{
            fontFamily: PJK,
            fontWeight: 700,
            fontSize: 16,
            lineHeight: "24px",
            color: "#E5E2E1",
          }}
        >
          {name}
        </span>
        <span
          style={{
            fontFamily: PJK,
            fontWeight: 500,
            fontSize: 10,
            lineHeight: "15px",
            color: "#968DA1",
          }}
        >
          {tasksCompleted.toLocaleString()} tasks completed
        </span>
      </div>

      {/* Tags */}
      <div style={{ display: "flex", gap: 4 }}>
        {tags.map((tag) => (
          <span
            key={tag}
            style={{
              background: isBestMatch
                ? "rgba(134,187,216,0.1)"
                : "#201F1F",
              color: isBestMatch ? "#86BBD8" : "#E5E2E1",
              borderRadius: 9999,
              padding: "2px 8px",
              fontFamily: PJK,
              fontWeight: isBestMatch ? 700 : 400,
              fontSize: 9,
              lineHeight: "13.5px",
            }}
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Bottom: status/price + select button */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          paddingTop: 16,
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 9999,
                background: "#22C55E",
                display: "inline-block",
              }}
            />
            <span
              style={{
                fontFamily: PJK,
                fontWeight: 700,
                fontSize: 10,
                lineHeight: "15px",
                color: "#22C55E",
              }}
            >
              Available
            </span>
          </div>

          <span
            style={{
              fontFamily: PJK,
              fontWeight: 700,
              fontSize: 18,
              lineHeight: "24px",
              color: isBestMatch ? "#F6AE2D" : "#E5E2E1",
            }}
          >
            ${price}
          </span>
        </div>

        <button
          onClick={handleSelect}
          className={isBestMatch ? "app-btn-primary" : "app-btn-secondary"}
          style={
            isBestMatch
              ? {
                  background: "#8635F6",
                  borderRadius: 40,
                  padding: "10px 0",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: PJK,
                  fontWeight: 700,
                  fontSize: 12,
                  lineHeight: "16px",
                  color: "#F2E6FF",
                  textAlign: "center",
                }
              : {
                  background: "transparent",
                  border: "2px solid #2E2A33",
                  borderRadius: 40,
                  padding: "12px 2px",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontFamily: PJK,
                  fontWeight: 700,
                  fontSize: 12,
                  lineHeight: "16px",
                  color: "#E5E2E1",
                  textAlign: "center",
                }
          }
        >
          Select
        </button>
      </div>
    </div>
  );
}
